// Admin-only route component
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '@hooks';
import { ROUTES } from '@constants';
import { UserRole } from '@types';

export const AdminRoute = () => {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Development bypass - skip authentication in development mode
  const isDevelopment = import.meta.env.VITE_ENV === 'development';
  const bypassAuth = isDevelopment && import.meta.env.VITE_BYPASS_AUTH !== 'false';

  if (bypassAuth) {
    console.log('⚠️ Development mode: Bypassing admin check');
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-sf-blue"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    console.log('Admin route: User not authenticated, redirecting to login');
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // User-management routes are admin-only
  const allowedRoles = [UserRole.ADMIN];
  const hasAccess = user?.role && allowedRoles.includes(user.role);

  if (!hasAccess) {
    console.log(`Admin route: User role is ${user?.role}, access denied`);
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page. This area is restricted to administrators.
          </p>
          <a
            href={ROUTES.DASHBOARD}
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  console.log(`Admin route: Access granted for ${user?.role} user`);
  return <Outlet />;
};
